import React from 'react';
import { makeStyles, Typography, Tooltip, Chip, Icon, Box } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  locationChip: {
    backgroundColor: theme.palette.type === 'dark' ? '#2d3748' : '#f7fafc',
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#2d3748',
    fontWeight: 500,
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    border: `1px solid ${theme.palette.type === 'dark' ? '#4a5568' : '#e2e8f0'}`,
    borderRadius: 6,
    padding: 0,
    height: 28,
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.06)',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#4a5568' : '#edf2f7',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)',
      transform: 'translateY(-1px)',
    },
    '& .MuiChip-label': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  chipSmall: {
    height: 22,
  },
  chipLarge: {
    height: 34,
  },
  chipContent: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 10px 0 0',
    height: '100%',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    width: 28,
    height: 28,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
    marginRight: 8,
  },
  iconWrapperSmall: {
    width: 22,
    height: 22,
    marginRight: 6,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
  },
  iconWrapperLarge: {
    width: 34,
    height: 34,
    marginRight: 10,
    borderTopLeftRadius: 5,
    borderBottomLeftRadius: 5,
  },
  locationIcon: {
    fontSize: 16,
  },
  locationIconSmall: {
    fontSize: 13,
  },
  locationIconLarge: {
    fontSize: 20,
  },
  label: {
    fontSize: 13,
    letterSpacing: 0.2,
    fontWeight: 500,
  },
  labelSmall: {
    fontSize: 11,
  },
  labelLarge: {
    fontSize: 15,
  },
  plainText: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#2d3748',
  },
  plainTextSmall: {
    fontSize: 11,
  },
  plainTextLarge: {
    fontSize: 15,
  },
  plainTextIconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    width: 20,
    height: 20,
    borderRadius: 4,
    marginRight: 6,
  },
  plainTextIconWrapperSmall: {
    width: 16,
    height: 16,
    marginRight: 4,
  },
  plainTextIconWrapperLarge: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  plainTextIcon: {
    fontSize: 14,
  },
  plainTextIconSmall: {
    fontSize: 12,
  },
  plainTextIconLarge: {
    fontSize: 18,
  },
}));

/**
 * Reusable component for displaying location information consistently across the application
 *
 * @param {Object} props Component props
 * @param {string} props.city The city name
 * @param {string} props.state The state name or code
 * @param {boolean} [props.chip=false] Whether to display as a chip or plain text (default)
 * @param {string} [props.tooltipText="Location"] Custom tooltip text
 * @param {string} [props.size="default"] Size of the component: "small", "default", or "large"
 */
export default function LocationDisplay({
  city,
  state,
  chip = false,
  tooltipText = 'Location',
  size = 'default',
  ...otherProps
}) {
  const classes = useStyles();

  if (!city || !state) return null;

  const locationText = `${city}, ${state}`;

  // Determine classes based on size
  const getChipClass = () => {
    if (size === 'small') return `${classes.locationChip} ${classes.chipSmall}`;
    if (size === 'large') return `${classes.locationChip} ${classes.chipLarge}`;
    return classes.locationChip;
  };

  const getIconWrapperClass = () => {
    if (size === 'small') return `${classes.iconWrapper} ${classes.iconWrapperSmall}`;
    if (size === 'large') return `${classes.iconWrapper} ${classes.iconWrapperLarge}`;
    return classes.iconWrapper;
  };

  const getIconClass = () => {
    if (size === 'small') return `${classes.locationIcon} ${classes.locationIconSmall}`;
    if (size === 'large') return `${classes.locationIcon} ${classes.locationIconLarge}`;
    return classes.locationIcon;
  };

  const getLabelClass = () => {
    if (size === 'small') return `${classes.label} ${classes.labelSmall}`;
    if (size === 'large') return `${classes.label} ${classes.labelLarge}`;
    return classes.label;
  };

  const getPlainTextClass = () => {
    if (size === 'small') return `${classes.plainText} ${classes.plainTextSmall}`;
    if (size === 'large') return `${classes.plainText} ${classes.plainTextLarge}`;
    return classes.plainText;
  };

  const getPlainTextIconWrapperClass = () => {
    if (size === 'small') return `${classes.plainTextIconWrapper} ${classes.plainTextIconWrapperSmall}`;
    if (size === 'large') return `${classes.plainTextIconWrapper} ${classes.plainTextIconWrapperLarge}`;
    return classes.plainTextIconWrapper;
  };

  const getPlainTextIconClass = () => {
    if (size === 'small') return `${classes.plainTextIcon} ${classes.plainTextIconSmall}`;
    if (size === 'large') return `${classes.plainTextIcon} ${classes.plainTextIconLarge}`;
    return classes.plainTextIcon;
  };

  const content = chip ? (
    <Chip
      className={getChipClass()}
      label={
        <Box className={classes.chipContent}>
          <Box className={getIconWrapperClass()}>
            <Icon className={getIconClass()}>location_on</Icon>
          </Box>
          <Typography component='span' className={getLabelClass()}>
            {locationText}
          </Typography>
        </Box>
      }
      {...otherProps}
    />
  ) : (
    <Box className={getPlainTextClass()} {...otherProps}>
      <Box className={getPlainTextIconWrapperClass()}>
        <Icon className={getPlainTextIconClass()}>location_on</Icon>
      </Box>
      <Typography component='span' className={getLabelClass()}>
        {locationText}
      </Typography>
    </Box>
  );

  return <Tooltip title={tooltipText}>{content}</Tooltip>;
}
